import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  Button, Formblock, FormSection, Message, Textfield,
} from '@zolteam/zol-admin-ui';
import { Form } from '@zolteam/zol-react-form';
import { useGetTerminal } from '../../../services/airports/terminals/adminTerminalQueries';
import { useTerminals } from '../../../domain/airports/terminals/useTerminals';
import { createTerminal, updateTerminal } from '../../../services/airports/terminals/adminTerminalService';
import fr from '../../../locales/fr.json';

const TERMINAL_FORM = 'terminalForm';

/**
 * @returns {JSX.Element}
 */
export default function TerminalNew() {
  const { id: airportId, terminalId } = useParams();

  const searchParams = new URLSearchParams(window.location.search);
  const flightBriefId = searchParams.get('flightBriefId');
  const bookingId = searchParams.get('bookingId');

  // local state
  const [name, setName] = useState('');
  const [identityCode, setIdentityCode] = useState('');
  const [address, setAddress] = useState('');
  const [coordinate, setCoordinate] = useState('');
  const [phone, setPhone] = useState('');
  const [openingTime, setOpeningTime] = useState('');
  const [closingTime, setClosingTime] = useState('');

  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: terminal,
  } = useGetTerminal(
    airportId,
    terminalId,
    { options: { enabled: !!terminalId } },
  );
  const {
    TERMINAL_FORM_SCHEMA,
    terminalDefaultValues,
    terminalFormDataToPayload,
  } = useTerminals({
    terminal,
  });

  const postCreateTerminalMutation = useMutation(createTerminal, {
    onSuccess: async (data) => {
      if (bookingId && flightBriefId) {
        navigate(`/bookings/${bookingId}/flight-brief/${flightBriefId}`, { replace: true });
      } else {
        const newId = data?.id ?? null;
        if (newId != null) {
          navigate(`/airports/${airportId}/`);
        } else {
          navigate(localStorage.getItem('airportListUrl') || '/airports');
        }
      }
    },
  });

  const putUpdateTerminalMutation = useMutation(updateTerminal, {
    onSuccess: async () => {
      if (bookingId && flightBriefId) {
        navigate(`/bookings/${bookingId}/flight-brief/${flightBriefId}`, { replace: true });
      } else {
        navigate(`/airports/${airportId}`);
      }
    },
  });

  // Handlers
  /**
   * Handles the form submission to create a new user.
   * @param {any} formValues - The values from the form.
   * @returns {void}
   */
  const onSubmit = (formValues) => {
    if (formValues) {
      const body = terminalFormDataToPayload({
        formValues,
      });
      if (terminalId) {
        putUpdateTerminalMutation.mutate({
          airportId,
          terminalId,
          terminal: Object.assign(body, { id: parseInt(terminalId, 10) }),
        });
      } else {
        postCreateTerminalMutation.mutate({ airportId, terminal: body });
      }
    }
  };

  /**
   * Set the status.
   * @param {string} newName - The status.
   * @param {any} formInfo
   */
  const handleSetName = (newName, formInfo) => {
    setName(newName);
    formInfo.formMethods.setValue('name', newName, { shouldValidate: true });
  };

  /**
   * Set the identity code.
   * @param {string} newIdentityCode - The identity code.
   * @param {any} formInfo
   */
  const handleSetIdentityCode = (newIdentityCode, formInfo) => {
    setIdentityCode(newIdentityCode);
    formInfo.formMethods.setValue('identityCode', newIdentityCode, { shouldValidate: true });
  };

  /**
   * Set the address.
   * @param {string} newAddress - The address.
   * @param {any} formInfo
   */
  const handleSetAddress = (newAddress, formInfo) => {
    setAddress(newAddress);
    formInfo.formMethods.setValue('address', newAddress, { shouldValidate: true });
  };

  /**
   * Set the latitude.
   * @param {string} newLatitude - The latitude.
   * @param {any} formInfo
   */
  /**
   * Set the coordinate as a comma separated string "lat,lon".
   * Also update the form value as an array [lat, lon].
   * @param {string} newCoordinate - The coordinate string
   * @param {any} formInfo
   */
  const handleSetCoordinate = (newCoordinate, formInfo) => {
    setCoordinate(newCoordinate);
    const parts = (newCoordinate || '').split(',').map((p) => p.trim());
    const lat = parts[0] || '';
    const lon = parts[1] || '';
    formInfo.formMethods.setValue('coordinate', [lat, lon], { shouldValidate: true });
  };

  /**
   * Set the phone.
   * @param {string} newPhone - The phone.
   * @param {any} formInfo
   */
  const handleSetPhone = (newPhone, formInfo) => {
    setPhone(newPhone);
    formInfo.formMethods.setValue('phone', newPhone, { shouldValidate: true });
  };

  /**
   * Set the opening time.
   * @param {string} newOpeningTime - The opening time.
   * @param {any} formInfo
   */
  const handleSetOpeningTime = (newOpeningTime, formInfo) => {
    setOpeningTime(newOpeningTime);
    formInfo.formMethods.setValue('openingTime', newOpeningTime, { shouldValidate: true });
  };

  /**
   * Set the closing time.
   * @param {string} newClosingTime - The closing time.
   * @param {any} formInfo
   */
  const handleSetClosingTime = (newClosingTime, formInfo) => {
    setClosingTime(newClosingTime);
    formInfo.formMethods.setValue('closingTime', newClosingTime, { shouldValidate: true });
  };

  useEffect(() => {
    if (terminal) {
      setName(terminal.name || '');
      setIdentityCode(terminal.identityCode || '');
      setAddress(terminal.address || '');
      setCoordinate((terminal.coordinate && terminal.coordinate.length)
        ? `${terminal.coordinate[0] || ''},${terminal.coordinate[1] || ''}`
        : '');
      setPhone(terminal.phone || '');
      setOpeningTime(terminal.openingTime || '');
      setClosingTime(terminal.closingTime || '');
    }
  }, [terminal]);

  return (
    <div className="padding-l relative">
      {/* button go back */}
      <button type="button" onClick={() => navigate(-1)}>
        {t('terminalForm.actions.back')}
      </button>
      <div
        id="form-header"
        className="d-flex f-row gap-s f-wrap justify-between padding-top-l sticky-top background-color-neutral-00 foreground-index"
      >
        <h1 className="h1-default" style={{ fontWeight: 'bold' }}>
          {terminal ? t('terminalForm.title.update') : t('terminalForm.title.new')}
        </h1>
      </div>

      <div className="form-content">
        {postCreateTerminalMutation.isError ? (
          <div className="margin-bottom-m">
            <Message>
              {t(`apiErrors.${postCreateTerminalMutation.error
                // @ts-ignore
                .code}`)}
            </Message>
          </div>
        ) : null}
        {putUpdateTerminalMutation.isError ? (
          <div className="margin-bottom-m">
            <Message>
              {t(`apiErrors.${putUpdateTerminalMutation.error
                // @ts-ignore
                .code}`)}
            </Message>
          </div>
        ) : null}

        {(terminalId && terminal) || !terminalId ? (
          <Form
            name={TERMINAL_FORM}
            callbacks={{ onSubmit }}
            schema={TERMINAL_FORM_SCHEMA}
            defaultValues={terminalDefaultValues}
            translations={{ formValidation: fr.formValidation }}
          >
            {(_formConfig, formInfo) => (
              <>
                <div>
                  <FormSection label={t('terminalForm.section.general')} className="margin-top-m">
                    <div className="d-flex f-row gap-m f-wrap align-end">
                      <Formblock
                        key="name"
                        htmlFor="name"
                        label={t('terminalForm.fields.name')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="name"
                          name="name"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.placeholders.name')}
                          value={name}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetName(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                      <Formblock
                        key="identityCode"
                        htmlFor="identityCode"
                        label={t('terminalForm.fields.identityCode')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="identityCode"
                          name="identityCode"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.placeholders.identityCode')}
                          value={identityCode}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetIdentityCode(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                      <Formblock
                        key="address"
                        htmlFor="address"
                        label={t('terminalForm.fields.address')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="address"
                          name="address"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.placeholders.address')}
                          value={address}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetAddress(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                    </div>
                    <div className="d-flex f-row gap-m f-wrap align-end">
                      <Formblock
                        key="coordinate"
                        htmlFor="coordinate"
                        label={t('terminalForm.fields.coordinate')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="coordinate"
                          name="coordinate"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.placeholders.coordinate')}
                          value={coordinate}
                          onChange={(
                            /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                          ) => handleSetCoordinate(e?.target?.value, formInfo)}
                        />
                      </Formblock>
                      <Formblock
                        key="phone"
                        htmlFor="phone"
                        label={t('terminalForm.fields.phone')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="phone"
                          name="phone"
                          type="tel"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.placeholders.phone')}
                          value={phone}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetPhone(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                    </div>
                    <div className="d-flex f-row gap-m f-wrap align-end">
                      <Formblock
                        key="openingTime"
                        htmlFor="openingTime"
                        label={t('terminalForm.fields.openingTime')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="openingTime"
                          name="openingTime"
                          type="time"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.placeholders.openingTime')}
                          value={openingTime}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetOpeningTime(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                      <Formblock
                        key="closingTime"
                        htmlFor="closingTime"
                        label={t('terminalForm.fields.closingTime')}
                        className="margin-bottom-xs"
                      >
                        <Textfield
                          // @ts-ignore
                          id="closingTime"
                          name="closingTime"
                          type="time"
                          className="background-neutral00"
                          placeholder={t('terminalForm.fields.placeholders.closingTime')}
                          value={closingTime}
                          onChange={
                            (
                              /** @type {import('react').ChangeEvent<HTMLInputElement>} */e,
                            ) => handleSetClosingTime(e?.target?.value, formInfo)
                          }
                        />
                      </Formblock>
                    </div>
                  </FormSection>
                </div>
                <div>
                  <Button
                    className="margin-top-m"
                    form={TERMINAL_FORM}
                    isSubmit
                    isLoading={
                      postCreateTerminalMutation.isLoading || putUpdateTerminalMutation.isLoading
                    }
                  >
                    {terminalId ? t('terminalForm.actions.update') : t('terminalForm.actions.submit')}
                  </Button>
                </div>
              </>
            )}
          </Form>
        ) : null}
      </div>
    </div>
  );
}
